import React from 'react';
import { ShoppingCart, Tag, Dumbbell, Package, ExternalLink, Zap } from 'lucide-react';
import { CENTR_PRODUCTS, CENTR_BUNDLES, CENTR_DISCOUNT, CENTR_LINKS } from '../../../lib/centrAffiliateData';
import { BookmarkButton } from '../../../components/BookmarkButton';
import { EQUIPMENT, safeId } from '../data';

const trackAffiliateClick = (p) => {
  try {
    window.dispatchEvent(new CustomEvent('track-affiliate', { detail: { product: p.name, url: p.url } }));
  } catch { /* ignore */ }
};

export const EquipmentSection = () => (
  <section id="equipment" className="container-ninja section-spacing" data-testid="hyrox-equipment-section">
    <div className="max-w-5xl mx-auto">
      <p className="text-[#e8ff00] text-xs uppercase tracking-wider font-heading mb-3 flex items-center gap-2">
        <ShoppingCart className="w-4 h-4" /> Official Equipment
      </p>
      <h2 className="text-3xl md:text-4xl text-white font-heading mb-2">CENTR x HYROX Equipment</h2>
      <p className="text-zinc-400 mb-2 max-w-2xl">
        Train on the exact equipment you&apos;ll race on. CENTR is the official HYROX equipment provider.
      </p>
      <div className="mb-8 bg-[#e8ff00]/5 border border-[#e8ff00]/30 px-5 py-3 flex items-center gap-3 max-w-lg" data-testid="centr-discount-banner">
        <Tag className="w-5 h-5 text-[#e8ff00] flex-shrink-0" />
        <div>
          <p className="text-white text-sm font-heading">
            Save {CENTR_DISCOUNT.percent}% — Use code <span className="text-[#e8ff00] bg-[#e8ff00]/10 px-1.5 py-0.5 mx-1">{CENTR_DISCOUNT.code}</span> at checkout
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {EQUIPMENT.map((e) => (
          <a
            key={e.name}
            href={e.affiliateUrl}
            target="_blank"
            rel="sponsored noopener noreferrer"
            className="group bg-zinc-900/80 border border-zinc-800 hover:border-zinc-600 p-5 transition-all block"
            data-testid={`equipment-${safeId(e.name)}`}
          >
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{e.station}</span>
                <h4 className="text-white font-heading text-lg group-hover:text-[#e8ff00] transition-colors">{e.name}</h4>
              </div>
              <span className="text-zinc-400 font-heading text-lg">{e.price}</span>
            </div>
            <p className="text-zinc-500 text-sm">{e.desc}</p>
            <div className="mt-3 flex items-center gap-1 text-zinc-500 text-xs font-heading uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
              Shop Now <ExternalLink className="w-3 h-3" />
            </div>
          </a>
        ))}
      </div>

      <h3 className="text-white font-heading text-xl mb-4 flex items-center gap-2">
        <Dumbbell className="w-4 h-4 text-[#e8ff00]" /> CENTR Equipment
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {CENTR_PRODUCTS.map((p) => (
          <a
            key={p.id}
            href={p.url}
            target="_blank"
            rel="sponsored noopener noreferrer"
            className="group bg-zinc-900/80 border border-[#e8ff00]/10 hover:border-[#e8ff00]/40 p-5 transition-all block relative"
            data-testid={`centr-product-${p.id}`}
            onClick={() => trackAffiliateClick(p)}
          >
            <div className="absolute top-3 right-3 flex items-center gap-1.5">
              <BookmarkButton item={{ type: 'product', item_id: p.id, title: p.name, price: p.price, url: p.url, category: 'HYROX Equipment' }} />
              <span className="bg-[#e8ff00] text-black text-[9px] uppercase tracking-wider font-heading px-2 py-0.5">
                {CENTR_DISCOUNT.percent}% OFF
              </span>
            </div>
            <div className="flex items-start justify-between mb-2 pr-16">
              <div>
                <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{p.station}</span>
                <h4 className="text-white font-heading text-lg group-hover:text-[#e8ff00] transition-colors">{p.name}</h4>
              </div>
            </div>
            <p className="text-zinc-500 text-sm mb-3">{p.desc}</p>
            <div className="flex items-center justify-between">
              <span className="text-[#e8ff00] font-heading text-lg">{p.price}</span>
              <span className="flex items-center gap-1 text-[#e8ff00] text-xs font-heading uppercase tracking-wider">
                Shop Now <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </a>
        ))}
      </div>

      <h3 className="text-white font-heading text-xl mb-4 flex items-center gap-2">
        <Package className="w-4 h-4 text-[#e8ff00]" /> HYROX Training Bundles
      </h3>
      <p className="text-zinc-500 text-sm mb-4">Complete equipment packages — everything you need in one purchase.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {CENTR_BUNDLES.map((b) => (
          <a
            key={b.id}
            href={b.url}
            target="_blank"
            rel="sponsored noopener noreferrer"
            className="group bg-zinc-900/80 border border-[#e8ff00]/10 hover:border-[#e8ff00]/40 p-5 transition-all block relative"
            data-testid={`centr-bundle-${b.id}`}
          >
            <div className="absolute top-3 right-3 bg-[#e8ff00] text-black text-[9px] uppercase tracking-wider font-heading px-2 py-0.5">
              {CENTR_DISCOUNT.percent}% OFF
            </div>
            <h4 className="text-white font-heading text-base group-hover:text-[#e8ff00] transition-colors pr-14 mb-2">{b.name}</h4>
            <p className="text-zinc-500 text-xs mb-3 leading-relaxed">{b.desc}</p>
            <div className="flex items-center justify-between pt-3 border-t border-zinc-800/50">
              <span className="text-[#e8ff00] font-heading text-lg">{b.price}</span>
              <span className="flex items-center gap-1 text-[#e8ff00] text-xs font-heading uppercase tracking-wider">
                Shop <ExternalLink className="w-3 h-3" />
              </span>
            </div>
          </a>
        ))}
      </div>

      <div className="bg-zinc-900/80 border border-[#e8ff00]/20 p-5 flex items-center gap-4" data-testid="centr-discount-footer">
        <Zap className="w-5 h-5 text-[#e8ff00] flex-shrink-0" />
        <div className="flex-1">
          <p className="text-white text-sm font-medium">NinjAthlete Exclusive</p>
          <p className="text-zinc-400 text-xs">
            Save {CENTR_DISCOUNT.percent}% on all CENTR x HYROX equipment with code <span className="text-[#e8ff00] font-heading">{CENTR_DISCOUNT.code}</span> — applied automatically through our links.
          </p>
        </div>
        <a
          href={CENTR_LINKS.home}
          target="_blank"
          rel="sponsored noopener noreferrer"
          className="flex-shrink-0 px-5 py-2.5 bg-[#e8ff00] text-black text-xs font-heading uppercase tracking-wider hover:opacity-90 transition-opacity"
          data-testid="centr-shop-all-btn"
        >
          Shop All
        </a>
      </div>
    </div>
  </section>
);
